import { Map } from "maplibre-gl";
import { FeatureCollection, Point } from "geojson";
import { LayerParams } from "../../map/layer-manager";
import { ColorType } from "../../models/color-type";
import { calcHeatmapGrid, HeatmapCalcStrategy } from "../../utils/heatmap-calc-strategy";
import { MapLayer } from "../map-layer";

/**
 * Camada do heatmap da chuva.
 */
export class HeatmapLayer extends MapLayer {

  constructor(private sourceId: string, private layerId: string) {
    super();
  }

  hasData(layerParams: LayerParams): boolean {
    let { rainData } = layerParams;
    return rainData != null && rainData.features.length > 0;
  }

  remove(map: Map) {

    if (map.getLayer(this.layerId)) {
      map.removeLayer(this.layerId);
    }

    if (map.getSource(this.sourceId)) {
      map.removeSource(this.sourceId);
    }
  }

  /**
   * Exibe a camada do heatmap, que pode usar o esquema de cores contínua ou discreta.
   * 
   * @param layerParams parâmetros para exibir a camada.
   */
  show(map: Map, layerParams: LayerParams) {

    const { rainData, tilesize, colorType } = layerParams;

    if (rainData == null || rainData.features.length == 0) {
      return;
    }

    // Adicionar estações de borda superior esquerda e inferior direita
    // para a grade de interpolação.
    this.addBorderStations(rainData);

    // Calcula o campo a ser usado para gerar a grade de interpolação.
    const calcStrategy = HeatmapCalcStrategy.factory(layerParams);
    calcStrategy(layerParams);

    // Gera a grade de interpolação com o módulo turf.
    const grid = calcHeatmapGrid(rainData, tilesize);

    // Cria a especificação da camada de acordo com o tipo de coloração.
    const layerSpec = this.getHeatmapLayerSpec(colorType);

    // Adiciona data source e layer.
    map.addSource(this.sourceId, {
      'type': 'geojson',
      'data': grid,
    });

    map.addLayer(layerSpec);
  }

  /**
   * Adiciona bordas auxiliares para adicionar um "padding" à grade da interpolação que será gerada
   * pelo turf.
   * São adicionadas uma borda superior esquerda e uma borda inferior direita, ambas com o 
   * índice pluviométrico igual a -1, o id de tempo igual a 999999 e o valor avg igual a 0. 
   * 
   * @param rainData dados das estações às quais serão adicionadas duas estações artificiais auxiliares.
   */
  addBorderStations(rainData: FeatureCollection<Point>) {

    let stations = rainData.features;
    let lefttop = [Number.MAX_SAFE_INTEGER, Number.MIN_SAFE_INTEGER];
    let rightbot = [Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER];

    stations.forEach((values) => {

      if (values.geometry.coordinates[0] < lefttop[0]) {
        lefttop[0] = values.geometry.coordinates[0];
      }
      if (values.geometry.coordinates[1] > lefttop[1]) {
        lefttop[1] = values.geometry.coordinates[1];
      }
      if (values.geometry.coordinates[0] > rightbot[0]) {
        rightbot[0] = values.geometry.coordinates[0];
      }
      if (values.geometry.coordinates[1] < rightbot[1]) {
        rightbot[1] = values.geometry.coordinates[1];
      }
    });

    stations.push({
      'type': 'Feature', 'geometry': { 'type': 'Point', 'coordinates': [lefttop[0] - 0.03, lefttop[1] + 0.03] }, 'properties': {
        'id_tempo': 999999,
        "indice_pluv": -1, "quinzemin": -1, "trintamin": -1, "umahora": -1, "seishoras": -1, "dozehoras": -1, "vintequatrohoras": -1, "quarentaoitohoras": -1, "setentaduashoras": -1, "noventaseishoras": -1, "mes": -1,
        'total': -1, 'percent': -1, 'nfraca': -1, 'nmoderada': -1, 'nforte': -1, 'nmuitoforte': -1, 'mean': -1, 'mode': -1, 'median': -1, 'avg': 0
      }
    });
    stations.push({
      'type': 'Feature', 'geometry': { 'type': 'Point', 'coordinates': [rightbot[0] + 0.046, rightbot[1] - 0.03] }, 'properties': {
        'id_tempo': 999999,
        "indice_pluv": -1, "quinzemin": -1, "trintamin": -1, "umahora": -1, "seishoras": -1, "dozehoras": -1, "vintequatrohoras": -1, "quarentaoitohoras": -1, "setentaduashoras": -1, "noventaseishoras": -1, "mes": -1,
        'total': -1, 'percent': -1, 'nfraca': -1, 'nmoderada': -1, 'nforte': -1, 'nmuitoforte': -1, 'mean': -1, 'mode': -1, 'median': -1, 'avg': 0
      }
    });
  }

  /**
   * Obtém a especificação de uma camada de heatmap, discreta ou contínua.
   * @param colorType tipo da coloração.
   */
  private getHeatmapLayerSpec(colorType: ColorType): any {

    if (colorType == ColorType.DISCRETE) {
      return {
        'id': this.layerId,
        'type': 'fill',
        'source':this.sourceId,
        'paint': {
          'fill-color': ['interpolate',
            ['linear'],
            ['to-number', ['get', 'avg']],
            -1,
            'rgb(51,153,68)',
            2.49999999999,
            'rgb(51,153,68)',
            2.5,
            'rgb(255,204,0)',
            9.99999999999,
            'rgb(255,204,0)',
            10,
            'rgb(255,136,4)',
            49.99999999999,
            'rgb(255,136,4)',
            50,
            'rgb(221,34,51)',
          ],
          'fill-opacity': ['interpolate',
            ['linear'],
            ['to-number', ['get', 'avg']],
            -1,
            0,
            0,
            0.6,
            1,
            0.6,
          ],
          'fill-outline-color': 'rgba(255,0,0,0)'
        }
      };
    }

    return {
      'id': this.layerId,
      'type': 'fill',
      'source': this.sourceId,
      'paint': {
        'fill-color': ['interpolate',
          ['linear'],
          ['to-number', ['get', 'avg']],
          -1,
          'rgba(247,251,255,0)',
          0,
          'rgba(247,251,255,0)',
          1,
          'rgb(8,48,107)',
        ],
        'fill-opacity': ['interpolate',
          ['linear'],
          ['to-number', ['get', 'avg']],
          -1,
          0,
          0,
          0.2,
          1,
          0.6,
        ],
        'fill-outline-color': 'rgba(255,0,0,0)'
      }
    };
  }
  
}
